import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useContractFilter,
  useNaranjaXPaymentsMutation,
  useRegisterPayment,
} from '../../../hooks/useCoupons';
import { useBranches } from '../../../hooks/useBranches';
import { usePlans } from '../../../hooks/usePlans';
import { useGroups } from '../../../hooks/useGroups';
import { useRecLegalEntities } from '../../../hooks/useLegalEntities';
import { useSalePoints } from '../../../hooks/useSalePoints';
import { useUserLogged } from '../../../hooks/useUserLogged';

export const useRegisterPaymentTable = () => {
  const navigate = useNavigate();
  const { branch, uuid } = useUserLogged();
  const { data: branches } = useBranches();
  const { data: plans } = usePlans();
  const { data: groups } = useGroups();
  const { data: recLegalEntities } = useRecLegalEntities();
  const { data: salePoints } = useSalePoints();

  const [data, setData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [contratoSeleccionado, setContratoSeleccionado] = useState(null);
  const [customAmount, setCustomAmount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [selectedDate, setSelectedDate] = useState(getCurrentDate());
  const [selectedCobroDate, setSelectedCobroDate] = useState(getCurrentDate());
  const [consolidateDate, setConsolidateDate] = useState(getCurrentDate());
  const [entidadSeleccionada, setEntidadSeleccionada] = useState('');
  const [selectedSalePoint, setSelectedSalePoint] = useState(null);
  const [makeElectronicInvoice, setMakeElectronicInvoice] = useState(true);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const searchTimeoutRef = useRef(null);

  const [filter, setFilter] = useState({
    plan: '',
    contract_number: '0',
  });

  const [saveModal, setSaveModal] = useState({
    open: false,
    loading: false,
    result: null,
    error: null,
  });

  const contractFilterMutation = useContractFilter();
  const registerPaymentMutation = useRegisterPayment();
  const naranjaXPaymentsMutation = useNaranjaXPaymentsMutation();

  function getCurrentDate() {
    const date = new Date();
    const year = date.getFullYear();
    const month = (1 + date.getMonth()).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  useEffect(() => {
    if (plans && plans.length > 0 && !filter.plan) {
      setFilter((prev) => ({
        ...prev,
        plan: plans[0].uuid,
      }));
    }
  }, [plans, filter.plan]);

  useEffect(() => {
    if (recLegalEntities && recLegalEntities.length > 0 && !entidadSeleccionada) {
      setEntidadSeleccionada(recLegalEntities[0].uuid);
    }
  }, [recLegalEntities, entidadSeleccionada]);

  useEffect(() => {
    if (salePoints && salePoints.length > 0 && !selectedSalePoint) {
      const defaultSalePoint = salePoints.find(point => point.number === 9);
      const selectedPoint = defaultSalePoint || salePoints[0];
      setSelectedSalePoint(selectedPoint);
    }
  }, [salePoints, selectedSalePoint]);

  useEffect(() => {
    if (contractFilterMutation.data) {
      const contractsData =
        contractFilterMutation.data.coupons && Array.isArray(contractFilterMutation.data.coupons)
          ? contractFilterMutation.data.coupons
          : [];
      setData(contractsData);
      setIsSearching(false);
    }
  }, [contractFilterMutation.data]);

  useEffect(() => {
    if (!contractFilterMutation.isPending) {
      setIsSearching(false);
    }
  }, [contractFilterMutation.isPending]);

  const calculateDueStage = useCallback((contrato, cobroDate) => {
    if (!contrato) return null;

    const currentDate = new Date(cobroDate);
    const firstDue = new Date(contrato.first_due_date);
    const secondDue = new Date(contrato.second_due_date);
    const thirdDue = new Date(contrato.third_due_date);

    let dueStage = '';
    let amount = 0;

    if (currentDate <= firstDue) {
      dueStage = 'first_due';
      amount = contrato.first_due_amount;
    } else if (currentDate <= secondDue) {
      dueStage = 'second_due';
      amount = contrato.second_due_amount;
    } else if (currentDate <= thirdDue) {
      dueStage = 'third_due';
      amount = contrato.third_due_amount;
    } else {
      dueStage = 'generate_new_due';
    }

    return {
      ...contrato,
      dueStage,
      amount,
    };
  }, []);

  useEffect(() => {
    if (contratoSeleccionado && contratoSeleccionado.uuid) {
      const updatedContrato = calculateDueStage(contratoSeleccionado, selectedCobroDate);
      
      if (updatedContrato && contratoSeleccionado.dueStage !== updatedContrato.dueStage) {
        setContratoSeleccionado(updatedContrato);
      }
    }
  }, [selectedCobroDate, calculateDueStage, contratoSeleccionado]);

  const totalCupones = tableData.length;
  const totalAmount = tableData.reduce((acc, data) => acc + parseFloat(data.amount || 0), 0);

  const selectedEntity = recLegalEntities?.find((entity) => entity.uuid === entidadSeleccionada);
  const isNaranjaX = selectedEntity?.legal_business_name === 'Naranja X';

  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const handleFilter = useCallback(
    (event) => {
      const { name, value } = event.target;

      if (name === 'contract_number') {
        setSearchTerm(value);

        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }

        if (!value.trim() || value === '0') {
          setIsSearching(false);
          setData([]);
          return;
        }

        setIsSearching(true);

        const filterData = {
          plan: filter.plan,
          contract_number: value,
        };

        contractFilterMutation.mutate(filterData);
      } else {
        setFilter((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
    },
    [filter.plan, contractFilterMutation]
  );

  const handleContratoChange = useCallback(
    (event, newValue) => {
      if (newValue && typeof newValue === 'object' && newValue.uuid) {
        const updatedContrato = calculateDueStage(newValue, selectedCobroDate);

        if (newValue.fee_type_name === 'De Puntaje' || newValue.fee_type_name === 'Adjudicación') {
          setCustomAmount(updatedContrato.amount || 0);
        }

        const finalContrato = {
          ...updatedContrato,
          date: selectedDate + 'T00:00:00',
        };

        setContratoSeleccionado(finalContrato);
      } else {
        setContratoSeleccionado(null);
      }
    },
    [selectedCobroDate, selectedDate, calculateDueStage]
  );

  const handleImporteChange = useCallback(
    (event) => {
      const newValue = event.target.value;
      setCustomAmount(newValue);

      if (contratoSeleccionado) {
        const updatedContrato = {
          ...contratoSeleccionado,
          first_due_amount: newValue,
          second_due_amount: newValue,
          third_due_amount: newValue,
          amount: newValue,
        };
        setContratoSeleccionado(updatedContrato);
      }
    },
    [contratoSeleccionado]
  );

  const handleRegistrarClick = useCallback(
    (contrato) => {
      if (!contrato) return;

      if (Array.isArray(contrato)) {
        setTableData((prev) => [...prev, ...contrato]);
        return;
      }

      const isDuplicate = tableData.some((item) => item.uuid === contrato.uuid);
      if (isDuplicate) {
        setShowDuplicateAlert(true);
        return;
      }

      if (contrato.dueStage === 'generate_new_due') {
        showSnackbar('Debe generar un nuevo vencimiento', 'warning');
        return;
      }

      if ((contrato.fee_type_name === 'De Puntaje' || contrato.fee_type_name === 'Adjudicación') && (!customAmount || customAmount <= 0)) {
        showSnackbar('Debe ingresar un importe válido para el cupón de puntaje/adjudicación', 'error');
        return;
      }

      setTableData((prev) => [...prev, contrato]);
    },
    [tableData, customAmount, showSnackbar]
  );

  const handleDeleteCoupon = useCallback((uuid) => {
    setTableData((prev) => prev.filter((item) => item.uuid !== uuid));
  }, []);

  const handleSalePointChange = useCallback(
    (event) => {
      const selectedPoint = salePoints?.find((point) => point.uuid === event.target.value);
      setSelectedSalePoint(selectedPoint);
    },
    [salePoints]
  );

  const handleConsolidate = useCallback(async () => {
    try {
      const result = await naranjaXPaymentsMutation.mutateAsync(consolidateDate);
      if (result.coupons && result.coupons.length > 0) {
        const processedCoupons = result.coupons.map((coupon) => ({
          ...coupon,
          date: consolidateDate + ' 00:00:00',
          dueStage: 'consolidated',
        }));

        handleRegistrarClick(processedCoupons);
        showSnackbar(
          `${result.coupons.length} cupones consolidados agregados exitosamente`,
          'success'
        );
      } else {
        showSnackbar('No se encontraron cupones consolidados para la fecha seleccionada', 'info');
      }
    } catch (error) {
      console.error('Error al consolidar pagos:', error);
      showSnackbar('Error al obtener los cupones consolidados', 'error');
    }
  }, [consolidateDate, naranjaXPaymentsMutation, handleRegistrarClick, showSnackbar]);

  const handleSaveCollection = useCallback(async () => {
    if (tableData.length === 0) {
      showSnackbar('No hay cupones para registrar', 'warning');
      return;
    }

    setSaveModal({
      open: true,
      loading: true,
      result: null,
      error: null,
    });

    try {
      const paymentData = {
        paymentsToRegister: tableData.map((item) => ({
          uuid: item.uuid,
          first_due_amount: item.amount,
          cancellation_date: isNaranjaX ? item.date : selectedCobroDate,
          uuid_legal_entity: entidadSeleccionada,
        })),
        invoiceData: {
          make_electronic_invoice: makeElectronicInvoice,
          token:
            'PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiIHN0YW5kYWxvbmU9InllcyI/Pgo8c3NvIHZlcnNpb249IjIuMCI+CiAgICA8aWQgc3JjPSJDTj13c2FhaG9tbywgTz1BRklQLCBDPUFSLCBTRVJJQUxOVU1CRVI9Q1VJVCAzMzY5MzQ1MDIzOSIgZHN0PSJDTj13c2ZlLCBPPUFGSVAsIEM9QVIiIHVuaXF1ZV9pZD0iMTQxNzAyMDg0NiIgZ2VuX3RpbWU9IjE3MDAyMzg4NjYiIGV4cF90aW1lPSIxNzAwMjgyMTI2Ii8+CiAgICA8b3BlcmF0aW9uIHR5cGU9ImxvZ2luIiB2YWx1ZT0iZ3JhbnRlZCI+CiAgICAgICAgPGxvZ2luIGVudGl0eT0iMzM2OTM0NTAyMzkiIHNlcnZpY2U9IndzZmUiIHVpZD0iU0VSSUFMTlVNQkVSPUNVSVQgMjAzNzYyMjgyMjQsIENOPWNvbG9uaWFsIiBhdXRobWV0aG9kPSJjbXMiIHJlZ21ldGhvZD0iMjIiPgogICAgICAgICAgICA8cmVsYXRpb25zPgogICAgICAgICAgICAgICAgPHJlbGF0aW9uIGtleT0iMjAzNzYyMjgyMjQiIHJlbHR5cGU9IjQiLz4KICAgICAgICAgICAgPC9yZWxhdGlvbnM+CiAgICAgICAgPC9sb2dpbj4KICAgIDwvb3BlcmF0aW9uPgo8L3Nzbz4K',
          sign: 'orAFUYOf8eSKu7RGVFhpSzmaM2Lv8pqJAzMTuNugfba6fBqx3nGP1wzXs8YFMqt/l3H1pYVFss9q0fBJHADweO8rFmexO5Jnqagv/AcJZ6TG4U7Kszwzmhc2xT99e8HELze0CgFdxyiAhE6Vd57w+VYAwErlq4RBXzuoecF/5jA=',
          uuid_user: uuid,
          uuid_branch: branch[0]?.uuid_branch,
          uuid_sale_point: selectedSalePoint?.uuid,
          entry_date: selectedDate,
          coupons: tableData.map((coupon) => ({ uuid_coupon: coupon.uuid })),
        },
      };

      const result = await registerPaymentMutation.mutateAsync(paymentData);

      setSaveModal({
        open: true,
        loading: false,
        result: result,
        error: null,
      });
    } catch (error) {
      console.error('Error during addRegisterPayment:', error);
      setSaveModal({
        open: true,
        loading: false,
        result: null,
        error: error,
      });
    }
  }, [
    tableData,
    entidadSeleccionada,
    makeElectronicInvoice,
    uuid,
    branch,
    selectedSalePoint,
    selectedDate,
    selectedCobroDate,
    isNaranjaX,
    registerPaymentMutation,
    showSnackbar,
  ]);

  const handleClearAll = useCallback(() => {
    setTableData([]);
    setFilter({
      plan: plans?.[0]?.uuid || '',
      contract_number: '0',
    });
    setContratoSeleccionado(null);
    setSearchTerm('');
    setCustomAmount(0);
    setData([]);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (recLegalEntities && recLegalEntities.length > 0) {
      setEntidadSeleccionada(recLegalEntities[0].uuid);
    }
    if (salePoints && salePoints.length > 0) {
      const defaultSalePoint = salePoints.find(point => point.number === 9);
      const selectedPoint = defaultSalePoint || salePoints[0];
      setSelectedSalePoint(selectedPoint);
    }

    setSelectedDate(getCurrentDate());
    setSelectedCobroDate(getCurrentDate());
    setConsolidateDate(getCurrentDate());
    setMakeElectronicInvoice(true);
    showSnackbar('Formulario limpiado exitosamente', 'success');
  }, [plans, recLegalEntities, salePoints, showSnackbar]);

  const closeSaveModal = useCallback(() => {
    setSaveModal({
      open: false,
      loading: false,
      result: null,
      error: null,
    });
  }, []);

  const handleNavigateToPayments = useCallback(() => {
    navigate('/cupon/registrar/ver-cobros');
  }, [navigate]);

  return {
    data,
    isLoading: contractFilterMutation.isPending,
    filter,

    tableData,
    totalCupones,
    totalAmount,
    contratoSeleccionado,
    customAmount,
    searchTerm,
    isSearching,
    consolidateDate,
    selectedDate,
    selectedCobroDate,
    entidadSeleccionada,
    selectedSalePoint,
    makeElectronicInvoice,
    saveModal,
    showDuplicateAlert,
    snackbar,
    isNaranjaX,

    plans: plans || [],
    groups: groups || [],
    branches: branches || [],
    recLegalEntities: recLegalEntities || [],
    salePoints: salePoints || [],

    handleFilter,
    setTableData,
    setSearchTerm,
    setIsSearching,
    setConsolidateDate,
    setSelectedDate,
    setSelectedCobroDate,
    setEntidadSeleccionada,
    setMakeElectronicInvoice,
    setShowDuplicateAlert,
    handleCloseSnackbar,
    handleContratoChange,
    handleImporteChange,
    handleRegistrarClick,
    handleDeleteCoupon,
    handleSalePointChange,
    handleConsolidate,
    handleSaveCollection,
    handleClearAll,
    closeSaveModal,
    handleNavigateToPayments,

    searchTimeoutRef,
  };
};
